import React, { useEffect, useState } from "react"
import { MdCheckCircle, MdEmail, MdErrorOutline, MdOutlineBlock, MdOutlineMarkEmailRead, MdSecurity } from "react-icons/md"
import { useSearchParams } from "react-router-dom"
import Button from "../../components/Form/Button"
import Logo from "../../logo.svg"
import api from "../../services/api"
import styles from "./emailPreferences.module.css"

const states = {
    loading: { icon: <MdEmail />, title: "Validando seu link", message: "Estamos conferindo os dados deste comunicado." },
    invalid: { icon: <MdErrorOutline />, title: "Link inválido ou expirado", message: "Use o link de descadastro de um comunicado recente ou entre em contato com a Oktto." },
    ready: { icon: <MdOutlineBlock />, title: "Descadastrar e-mail", message: "Você deixará de receber comunicados de marketing enviados pela Oktto." },
    complete: { icon: <MdCheckCircle />, title: "Descadastro confirmado", message: "Este endereço não receberá novos comunicados de marketing." },
}

const Unsubscribe = () => {
    const [searchParams] = useSearchParams()
    const token = searchParams.get("token") || ""
    const [status, setStatus] = useState("loading")
    const [details, setDetails] = useState(null)
    const [message, setMessage] = useState("")
    const [submitting, setSubmitting] = useState(false)
    const [requestingReactivation, setRequestingReactivation] = useState(false)
    const [reactivationRequested, setReactivationRequested] = useState(false)

    useEffect(() => {
        let active = true
        const validate = async () => {
            if (!token) {
                setStatus("invalid")
                return
            }
            try {
                const response = await api.get("/email-preferences/unsubscribe", {
                    params: { token },
                    headers: { Accept: "application/json" },
                })
                if (!active) return
                if (!response.data?.data?.email_masked) throw new Error("Resposta inválida ao validar o link.")
                setDetails(response.data.data)
                setReactivationRequested(Boolean(response.data.data.reactivation_pending))
                setStatus(response.data.data.already_unsubscribed ? "complete" : "ready")
            } catch (err) {
                if (!active) return
                setMessage(err.response?.data?.message || "Não foi possível validar este link.")
                setStatus("invalid")
            }
        }
        validate()
        return () => { active = false }
    }, [token])

    const confirm = async () => {
        setSubmitting(true)
        setMessage("")
        try {
            const response = await api.post("/email-preferences/unsubscribe", { token }, { headers: { Accept: "application/json" } })
            setMessage(response.data?.message || "Descadastro confirmado.")
            setDetails(current => ({ ...current, already_unsubscribed: true, can_request_reactivation: Boolean(response.data?.can_request_reactivation) }))
            setStatus("complete")
        } catch (err) {
            setMessage(err.response?.data?.message || "Não foi possível concluir o descadastro. Tente novamente.")
        } finally {
            setSubmitting(false)
        }
    }

    const requestReactivation = async () => {
        setRequestingReactivation(true)
        setMessage("")
        try {
            const response = await api.post("/email-preferences/resubscribe/request", { unsubscribe_token: token }, { headers: { Accept: "application/json" } })
            setMessage(response.data?.message || "Enviamos um e-mail para confirmar a reativação.")
            setReactivationRequested(true)
        } catch (err) {
            setMessage(err.response?.data?.message || "Não foi possível enviar a confirmação. Tente novamente.")
            if (err.response?.status === 429) setReactivationRequested(true)
        } finally {
            setRequestingReactivation(false)
        }
    }

    const content = states[status]
    return (
        <main className={styles.page}>
            <section className={styles.card} aria-live="polite">
                <header className={styles.header}>
                    <img src={Logo} alt="Oktto" />
                    <span>Preferências de comunicação</span>
                </header>

                <div className={`${styles.statusIcon} ${status === "complete" ? styles.successIcon : status === "invalid" ? styles.errorIcon : ""}`}>
                    {content.icon}
                </div>
                <h1>{content.title}</h1>
                <p className={styles.lead}>{message || content.message}</p>

                {status === "loading" && <div className={styles.loader} aria-label="Carregando" />}

                {status === "ready" && (
                    <>
                        <div className={styles.recipient}>
                            <span>Endereço</span>
                            <strong>{details?.email_masked || "E-mail do destinatário"}</strong>
                        </div>
                        <div className={styles.notice}>
                            <MdSecurity />
                            <div>
                                <strong>E-mails necessários continuam ativos</strong>
                                <span>Acesso, segurança, cobrança, boletos e mensagens operacionais não serão bloqueados.</span>
                            </div>
                        </div>
                        <Button type="button" onClick={confirm} disabled={submitting}>
                            <MdOutlineBlock /> {submitting ? "Confirmando..." : "Confirmar descadastro"}
                        </Button>
                        <p className={styles.hint}>Nenhuma alteração será feita antes da sua confirmação.</p>
                    </>
                )}

                {status === "complete" && details?.email_masked && (
                    <>
                        <div className={styles.recipient}>
                            <span>Endereço descadastrado</span>
                            <strong>{details.email_masked}</strong>
                        </div>
                        {reactivationRequested && (
                            <div className={styles.notice}>
                                <MdOutlineMarkEmailRead />
                                <div>
                                    <strong>Confirmação enviada</strong>
                                    <span>A preferência continuará bloqueada até a confirmação pelo e-mail.</span>
                                </div>
                            </div>
                        )}
                        {!reactivationRequested && details.can_request_reactivation && (
                            <div className={styles.reactivation}>
                                <span>Mudou de ideia? Enviaremos uma confirmação antes de alterar sua preferência.</span>
                                <Button type="button" variant="secondary" onClick={requestReactivation} disabled={requestingReactivation}>
                                    <MdOutlineMarkEmailRead /> {requestingReactivation ? "Enviando confirmação..." : "Quero voltar a receber"}
                                </Button>
                            </div>
                        )}
                    </>
                )}
            </section>
        </main>
    )
}

export default Unsubscribe
